import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { PostStatusBadge, PostTypePill } from "@/components/shared/Badges";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { publicAssetUrl } from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { POST_TYPES, POST_TYPE_LABEL } from "@/lib/labels";
import { Send, Archive, Search, Zap } from "lucide-react";
import BulkOffersDialog from "@/components/admin/BulkOffersDialog";

export default function AdminPostsPage() {
    const [sp, setSp] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusF, setStatusF] = useState(sp.get("status") || "__all__");
    const [typeF, setTypeF] = useState(sp.get("type") || "__all__");
    const [searchQ, setSearchQ] = useState(sp.get("search") || "");
    const [districts, setDistricts] = useState([]);
    const [districtF, setDistrictF] = useState(sp.get("district_slug") || "__all__");
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusF !== "__all__") params.set("status", statusF);
            if (typeF !== "__all__") params.set("type", typeF);
            if (districtF !== "__all__") params.set("district_slug", districtF);
            if (searchQ.trim()) params.set("search", searchQ.trim());
            params.set("limit", "200");
            setSp(params, { replace: true });
            const res = await api.get(`/admin/posts?${params.toString()}`);
            setRows(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [statusF, typeF, districtF, searchQ]); // eslint-disable-line

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);

    const publish = async (id) => {
        try {
            await api.post(`/posts/${id}/publish`);
            toast.success("Published.");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const archive = async (id) => {
        try {
            await api.post(`/posts/${id}/archive`);
            toast.success("Archived.");
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-posts-page">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-orange-600">Moderation</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                        All posts
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Every post across all statuses. Filter and moderate.
                    </p>
                </div>
                <Button
                    onClick={() => setBulkOpen(true)}
                    className="btn-primary rounded-xl"
                    data-testid="bulk-offers-btn"
                >
                    <Zap size={14} className="mr-2" /> Bulk create offers
                </Button>
            </header>

            <div className="grid gap-3 rounded-2xl border border-neutral-200 bg-white p-4 md:grid-cols-4">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
                    <Input
                        placeholder="Search title or description"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        className="pl-9"
                        data-testid="admin-posts-search"
                    />
                </div>
                <Select value={statusF} onValueChange={setStatusF}>
                    <SelectTrigger data-testid="admin-posts-status">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="PUBLISHED">Live</SelectItem>
                        <SelectItem value="EXPIRED">Expired</SelectItem>
                        <SelectItem value="ARCHIVED">Archived</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={typeF} onValueChange={setTypeF}>
                    <SelectTrigger data-testid="admin-posts-type">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All types</SelectItem>
                        {POST_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                                {POST_TYPE_LABEL[t]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={districtF} onValueChange={setDistrictF}>
                    <SelectTrigger data-testid="admin-posts-district">
                        <SelectValue placeholder="District" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All districts</SelectItem>
                        {districts.map((d) => (
                            <SelectItem key={d.slug} value={d.slug}>
                                {d.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <Skeleton className="h-96 rounded-2xl" />
            ) : rows.length === 0 ? (
                <EmptyState title="No posts match these filters" />
            ) : (
                <div className="grid gap-3">
                    {rows.map((p) => (
                        <div
                            key={p.id}
                            className="flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-3 sm:flex-row sm:items-center"
                            data-testid={`admin-post-row-${p.id}`}
                        >
                            <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl bg-neutral-100 sm:h-20 sm:w-32">
                                {p.poster_image_url ? (
                                    <img
                                        src={publicAssetUrl(p.poster_image_url)}
                                        alt={p.title}
                                        className="h-full w-full object-cover"
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center nest-gradient text-white text-xs font-bold">
                                        OfferNest
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Link to={`/posts/${p.id}`} className="font-semibold text-neutral-900 hover:underline">
                                        {p.title}
                                    </Link>
                                    <PostTypePill type={p.type} />
                                    <PostStatusBadge status={p.status} />
                                </div>
                                <div className="mt-1 text-xs text-neutral-500">
                                    <span className="capitalize">{p.district_slug}</span>
                                    {p.category_slug ? ` · ${p.category_slug.replace(/-/g, " ")}` : ""} ·{" "}
                                    by {p.business_name || p.created_by_role}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(p.status === "DRAFT" || p.status === "ARCHIVED") && (
                                    <Button
                                        size="sm"
                                        onClick={() => publish(p.id)}
                                        className="nest-gradient text-white"
                                        data-testid={`admin-publish-${p.id}`}
                                    >
                                        <Send size={13} className="mr-1" /> Publish
                                    </Button>
                                )}
                                {p.status !== "ARCHIVED" && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => archive(p.id)}
                                        data-testid={`admin-archive-${p.id}`}
                                    >
                                        <Archive size={13} className="mr-1" /> Archive
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <BulkOffersDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                onDone={load}
            />
        </div>
    );
}
